import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

interface SalesforceModule {
  title: string;
  category: string;
  description: string;
  features: string[];
  technologies: string[];
  href: string;
}

interface SalesforceMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const SalesforceMenu = ({ isOpen, onClose }: SalesforceMenuProps) => {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Group modules by category for better organization
  const moduleCategories = {
    "Core Clouds": [
      {
        title: "Sales Cloud",
        category: "Sales",
        description: "Lead management, opportunity tracking, and sales forecasting for comprehensive sales automation.",
        features: ["Lead Management", "Opportunity Tracking", "Sales Forecasting", "Territory Management"],
        technologies: ["Sales Cloud", "Einstein AI", "Salesforce Mobile", "Lightning Platform"],
        href: "/salesforce/sales-cloud"
      },
      {
        title: "Service Cloud",
        category: "Service",
        description: "Case management, knowledge base, and customer support for exceptional service delivery.",
        features: ["Case Management", "Knowledge Base", "Omni-Channel Routing", "Field Service"],
        technologies: ["Service Cloud", "Einstein Case Classification", "Live Agent", "Field Service Lightning"],
        href: "/salesforce/service-cloud"
      },
      {
        title: "Marketing Cloud",
        category: "Marketing",
        description: "Email marketing, campaign automation, and customer journeys for personalized marketing.",
        features: ["Email Marketing", "Customer Journeys", "Social Media Marketing", "Mobile Messaging"],
        technologies: ["Marketing Cloud", "Journey Builder", "Email Studio", "Social Studio"],
        href: "/salesforce/marketing-cloud"
      },
      {
        title: "Commerce Cloud",
        category: "Commerce",
        description: "E-commerce platform, product catalog, and order management for digital commerce.",
        features: ["E-commerce Platform", "Product Catalog", "Order Management", "Payment Processing"],
        technologies: ["Commerce Cloud", "B2C Commerce", "B2B Commerce", "Order Management"],
        href: "/salesforce/commerce-cloud"
      }
    ],
    "Platform & Development": [
      {
        title: "App Cloud (Platform)",
        category: "Platform",
        description: "Custom app development, workflow automation, and integration for platform solutions.",
        features: ["Custom App Development", "Workflow Automation", "Process Builder", "Lightning Components"],
        technologies: ["Lightning Platform", "Apex", "Visualforce", "Lightning Web Components"],
        href: "/salesforce/app-cloud"
      },
      {
        title: "Experience Cloud",
        category: "Experience",
        description: "Digital experiences, portal creation, and content management for customer experiences.",
        features: ["Digital Experiences", "Portal Creation", "Content Management", "Self-Service Portals"],
        technologies: ["Experience Cloud", "Community Builder", "CMS", "Portal Templates"],
        href: "/salesforce/experience-cloud"
      },
      {
        title: "DevOps Center",
        category: "DevOps",
        description: "Change management, deployment automation, and release management for development operations.",
        features: ["Change Management", "Deployment Automation", "Release Management", "Source Control"],
        technologies: ["DevOps Center", "Change Sets", "Deployment APIs", "Metadata API"],
        href: "/salesforce/devops"
      }
    ],
    "Analytics & Data": [
      {
        title: "Analytics Cloud",
        category: "Analytics",
        description: "Business intelligence, data visualization, and predictive analytics for data-driven insights.",
        features: ["Business Intelligence", "Data Visualization", "Predictive Analytics", "Mobile Analytics"],
        technologies: ["Tableau CRM", "Einstein Analytics", "Wave Analytics", "Analytics Studio"],
        href: "/salesforce/analytics-cloud"
      },
      {
        title: "Data Cloud",
        category: "Data",
        description: "Customer data platform, data integration, and identity resolution for unified data management.",
        features: ["Customer Data Platform", "Data Integration", "Identity Resolution", "Real-time Data"],
        technologies: ["Salesforce CDP", "Data Integration", "Customer 360", "Real-time APIs"],
        href: "/salesforce/data-cloud"
      },
      {
        title: "Tableau CRM",
        category: "Advanced Analytics",
        description: "Advanced analytics, AI-powered insights, and embedded analytics for business intelligence.",
        features: ["Advanced Analytics", "AI-powered Insights", "Embedded Analytics", "Predictive Modeling"],
        technologies: ["Tableau CRM", "Einstein Discovery", "Analytics APIs", "Embedded Dashboards"],
        href: "/salesforce/tableau-crm"
      }
    ],
    "Revenue & Financial": [
      {
        title: "Revenue Cloud",
        category: "Revenue",
        description: "Subscription management, billing automation, and revenue recognition for revenue operations.",
        features: ["Subscription Management", "Billing Automation", "Revenue Recognition", "Contract Management"],
        technologies: ["Revenue Cloud", "Billing", "Subscription Management", "Revenue Recognition"],
        href: "/salesforce/revenue-cloud"
      },
      {
        title: "CPQ (Configure Price Quote)",
        category: "Quoting",
        description: "Product configuration, pricing rules, and quote generation for sales automation.",
        features: ["Product Configuration", "Pricing Rules", "Quote Generation", "Approval Workflows"],
        technologies: ["Salesforce CPQ", "Product Configurator", "Pricing Engine", "Quote Templates"],
        href: "/salesforce/cpq"
      },
      {
        title: "Payments",
        category: "Payments",
        description: "Payment processing, transaction management, and financial operations for commerce solutions.",
        features: ["Payment Processing", "Transaction Management", "Fraud Detection", "Recurring Billing"],
        technologies: ["Salesforce Payments", "Payment Gateway", "Transaction APIs", "Billing Management"],
        href: "/salesforce/payments"
      }
    ],
    "Integration & Security": [
      {
        title: "Integration Cloud",
        category: "Integration",
        description: "API management, data synchronization, and process automation for seamless integration.",
        features: ["API Management", "Data Synchronization", "Process Automation", "Real-time Integration"],
        technologies: ["MuleSoft", "Salesforce Integration", "Platform Events", "Streaming API"],
        href: "/salesforce/integration"
      },
      {
        title: "Security & Identity",
        category: "Security",
        description: "Single sign-on, multi-factor authentication, and encryption for comprehensive security.",
        features: ["Single Sign-On", "Multi-factor Authentication", "Encryption", "Identity Management"],
        technologies: ["Salesforce Identity", "Shield Platform Encryption", "Event Monitoring", "Security Center"],
        href: "/salesforce/security"
      }
    ],
    "Industry Solutions": [
      {
        title: "Health Cloud",
        category: "Healthcare",
        description: "Patient management, care coordination, and provider networks for healthcare industry.",
        features: ["Patient Management", "Care Coordination", "Provider Networks", "Health Records"],
        technologies: ["Health Cloud", "Patient Management", "Care Plans", "Provider Directory"],
        href: "/salesforce/health-cloud"
      },
      {
        title: "Financial Services Cloud",
        category: "Financial",
        description: "Client management, financial planning, and compliance for financial services industry.",
        features: ["Client Management", "Financial Planning", "Compliance Management", "Wealth Management"],
        technologies: ["Financial Services Cloud", "Client Management", "Wealth Management", "Compliance"],
        href: "/salesforce/financial-services"
      },
      {
        title: "Education Cloud",
        category: "Education",
        description: "Student recruitment, academic advising, and alumni relations for education industry.",
        features: ["Student Recruitment", "Academic Advising", "Alumni Relations", "Student Success"],
        technologies: ["Education Cloud", "Student Success Hub", "Advisor Link", "Alumni Management"],
        href: "/salesforce/education-cloud"
      },
      {
        title: "Manufacturing Cloud",
        category: "Manufacturing",
        description: "Account planning, sales agreements, and partner management for manufacturing industry.",
        features: ["Account Planning", "Sales Agreements", "Partner Management", "Forecast Management"],
        technologies: ["Manufacturing Cloud", "Sales Agreements", "Account Forecasts", "Partner Portal"],
        href: "/salesforce/manufacturing"
      },
      {
        title: "Nonprofit Cloud",
        category: "Nonprofit",
        description: "Donor management, program tracking, and volunteer coordination for nonprofit organizations.",
        features: ["Donor Management", "Program Tracking", "Volunteer Coordination", "Grant Management"],
        technologies: ["Nonprofit Cloud", "NPSP", "Program Management Module", "Volunteer Workforce"],
        href: "/salesforce/nonprofit"
      }
    ],
    "Service & Field Operations": [
      {
        title: "Field Service",
        category: "Field Service",
        description: "Work order management, technician scheduling, and mobile access for field service operations.",
        features: ["Work Order Management", "Technician Scheduling", "Mobile Access", "Inventory Management"],
        technologies: ["Field Service Lightning", "Mobile App", "Scheduling Optimization", "Resource Management"],
        href: "/salesforce/field-service"
      },
      {
        title: "Transportation & Logistics",
        category: "Logistics",
        description: "Shipment tracking, route optimization, and carrier management for transportation industry.",
        features: ["Shipment Tracking", "Route Optimization", "Carrier Management", "Logistics Planning"],
        technologies: ["Transportation Management", "Logistics Cloud", "Route Planning", "Carrier Portal"],
        href: "/salesforce/transportation"
      },
      {
        title: "Scheduling",
        category: "Scheduling",
        description: "Appointment booking, resource management, and calendar integration for scheduling optimization.",
        features: ["Appointment Booking", "Resource Management", "Calendar Integration", "Automated Scheduling"],
        technologies: ["Salesforce Scheduler", "Lightning Scheduler", "Resource Calendar", "Booking APIs"],
        href: "/salesforce/scheduling"
      }
    ],
    "Communication & Engagement": [
      {
        title: "Digital Engagement",
        category: "Engagement",
        description: "Digital messaging, chat support, and omnichannel engagement for customer communication.",
        features: ["Digital Messaging", "Chat Support", "Video Support", "Social Customer Service"],
        technologies: ["Digital Engagement", "Live Message", "Video Support", "Social Studio"],
        href: "/salesforce/digital-engagement"
      },
      {
        title: "Community Cloud",
        category: "Community",
        description: "Partner portals, customer communities, and collaboration for community engagement.",
        features: ["Partner Portals", "Customer Communities", "Collaboration Tools", "Content Management"],
        technologies: ["Community Cloud", "Experience Cloud", "Partner Communities", "Customer Communities"],
        href: "/salesforce/community-cloud"
      }
    ],
    "Infrastructure & Innovation": [
      {
        title: "Hyperforce",
        category: "Infrastructure",
        description: "Cloud infrastructure, scalability, and global deployment for enterprise-grade hosting.",
        features: ["Cloud Infrastructure", "Global Scalability", "Data Residency", "Enterprise Security"],
        technologies: ["Hyperforce", "Multi-Cloud", "Global Infrastructure", "Security Controls"],
        href: "/salesforce/hyperforce"
      },
      {
        title: "Innovation Platform",
        category: "Innovation",
        description: "AI/ML capabilities, emerging technologies, and innovation tools for future-ready solutions.",
        features: ["AI/ML Capabilities", "Emerging Technologies", "Innovation Labs", "Experimental Features"],
        technologies: ["Einstein Platform", "Trailhead", "Innovation Zones", "Labs Features"],
        href: "/salesforce/innovation"
      },
      {
        title: "Work.com",
        category: "Workplace",
        description: "Employee wellness, workplace safety, and return-to-work solutions for workplace management.",
        features: ["Employee Wellness", "Workplace Safety", "Return-to-Work", "Health Screening"],
        technologies: ["Work.com", "Workplace Command Center", "Health Check", "Contact Tracing"],
        href: "/salesforce/work-com"
      }
    ]
  };

  const handleModuleClick = (module: SalesforceModule) => {
    navigate(module.href);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      ref={menuRef}
      className="fixed top-16 left-0 right-0 bg-background/95 backdrop-blur-sm border-b border-border shadow-xl z-[59]"
    >
      <div className="max-w-7xl mx-auto px-4 py-6">
        <h3 className="text-lg font-semibold text-foreground mb-6">Salesforce Clouds & Services</h3>
        <div className="max-h-96 overflow-y-auto space-y-6">
          {Object.entries(moduleCategories).map(([categoryName, modules]) => (
            <div key={categoryName} className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide border-b border-border pb-1">
                {categoryName}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                {modules.map((module, index) => (
                  <button
                    key={index}
                    onClick={() => handleModuleClick(module)}
                    className="text-left px-3 py-2 text-sm text-foreground hover:text-orange-600 hover:bg-orange-50/50 transition-colors duration-200 rounded-md"
                  >
                    <span className="font-medium text-xs">{module.title}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SalesforceMenu;